import React, { useState, useRef } from 'react';
import { Layout } from '@/components/layout';
import { Button, CompactSelect } from '@/components/ui-elements';
import { useAuthStore, useAuthHeaders } from '@/lib/auth-store';
import { useToast } from '@/hooks/use-toast';
import { useLocation } from 'wouter';
import { useGetMe } from '@workspace/api-client-react';
import { cn } from '@/lib/utils';
import {
  Globe,
  Download,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  Loader2,
  Package,
  Link,
  BarChart3,
  ShoppingBag,
  WifiOff,
  List,
  RotateCw,
  Wand2,
  FileText,
  Upload,
} from 'lucide-react';

const BASE = import.meta.env.BASE_URL.replace(/\/$/, '');

type Platform = 'bigcommerce' | 'neto' | 'shopify' | 'magento' | 'wordpress' | 'egr' | 'brown-davis' | 'auto';
type ScrapeMode = 'store' | 'urls' | 'csv';
const BROWN_DAVIS_EMAIL = 'marketing@bove.com.au';
const BROWN_DAVIS_URL = 'https://www.browndavis.com.au/products.php';

interface ProgressState {
  current: number;
  total: number;
  url: string;
  productsFound: number;
}

type MaxPages = 250 | 500 | 1000 | 2000 | 0;

function fmtSecs(s: number): string {
  if (!isFinite(s) || s <= 0) return '–';
  if (s < 60) return `${Math.round(s)}s`;
  const m = Math.floor(s / 60);
  const rem = Math.round(s % 60);
  return rem > 0 ? `${m}m ${rem}s` : `${m}m`;
}

function etaForMaxPages(n: MaxPages): string {
  if (n === 0) return '2–4 hours';
  const secs = n * 1.1;
  return fmtSecs(secs);
}

const PLATFORMS: {
  id: Platform;
  label: string;
  tagline: string;
  storePlaceholder: string;
  urlsPlaceholder: string;
  hint: string;
  storeHint: string;
  urlsHint: string;
  icon: React.ComponentType<{ className?: string }>;
}[] = [
  {
    id: 'bigcommerce',
    icon: ShoppingBag,
    label: 'BigCommerce',
    tagline: 'Reads the XML sitemap to find every product page, then extracts data via JSON-LD, BCData JS, and OG meta tags.',
    storePlaceholder: 'https://mystore.mybigcommerce.com',
    urlsPlaceholder: 'https://store.com/products/mountain-bike\nhttps://store.com/products/helmet\nhttps://store.com/category/bikes',
    hint: 'Works on any publicly accessible BigCommerce storefront — no API key required.',
    storeHint: 'Scrapes the entire store by reading its public XML sitemap.',
    urlsHint: 'Paste product or category page URLs — one per line. Category pages are scraped for all linked products.',
  },
  {
    id: 'neto',
    icon: ShoppingBag,
    label: 'Neto / Maropost',
    tagline: 'Decompresses the gzipped sitemap index, then extracts product data via GA4 data attributes and microdata.',
    storePlaceholder: 'https://mystore.neto.com.au',
    urlsPlaceholder: 'https://store.neto.com.au/nb_int/product-handle\nhttps://store.neto.com.au/nb_int/another-product',
    hint: 'Works on any publicly accessible Neto / Maropost Commerce Cloud storefront.',
    storeHint: 'Scrapes the entire store by reading its gzipped sitemap index.',
    urlsHint: 'Paste specific product page URLs from the Neto store — one per line.',
  },
  {
    id: 'shopify',
    icon: ShoppingBag,
    label: 'Shopify',
    tagline: 'Uses the public products.json API to retrieve all products with full variant, pricing, and inventory data.',
    storePlaceholder: 'https://mystore.myshopify.com',
    urlsPlaceholder: 'https://store.com/products/my-product\nhttps://store.com/collections/my-collection\nhttps://store.com/products/another-product',
    hint: 'Works on any publicly accessible Shopify storefront — no API key required.',
    storeHint: 'Fetches all products using the /products.json API — much faster than HTML scraping.',
    urlsHint: 'Paste specific product URLs or collection URLs — one per line. Collections are fully expanded.',
  },
  {
    id: 'magento',
    icon: ShoppingBag,
    label: 'Magento',
    tagline: 'Parses the XML sitemap index to discover all product pages, then extracts data via JSON-LD and Open Graph meta tags.',
    storePlaceholder: 'https://mystore.com.au',
    urlsPlaceholder: 'https://store.com/category-name.html\nhttps://store.com/product-name.html',
    hint: 'Works on any publicly accessible Magento 2 storefront — no API key required.',
    storeHint: 'Discovers all products via the sitemap.xml index, then scrapes each product page.',
    urlsHint: 'Paste product or category page URLs — one per line. Category pages are expanded for all linked products.',
  },
  {
    id: 'wordpress',
    icon: ShoppingBag,
    label: 'WordPress / WooCommerce',
    tagline: 'Walks the WooCommerce sitemap to discover products, then extracts data via JSON-LD schema and Open Graph meta tags.',
    storePlaceholder: 'https://mystore.com',
    urlsPlaceholder: 'https://store.com/product/mountain-bike\nhttps://store.com/product-category/bikes',
    hint: 'Works on any publicly accessible WooCommerce store — no API key required.',
    storeHint: 'Discovers all products via the WooCommerce product sitemap, then scrapes each product page.',
    urlsHint: 'Paste product or product-category page URLs — one per line. Category pages are expanded for all linked products.',
  },
  {
    id: 'egr',
    icon: ShoppingBag,
    label: 'EGR Auto',
    tagline: 'Reads product data directly from EGR Auto (egrauto.com) category pages — no API key required.',
    storePlaceholder: 'https://egrauto.com/products-catalog/fender-flares',
    urlsPlaceholder: 'https://egrauto.com/products-catalog/fender-flares\nhttps://egrauto.com/products-catalog/egr-rolltrac',
    hint: 'Works on any EGR Auto product category page.',
    storeHint: 'Paste an EGR Auto category URL — e.g. https://egrauto.com/products-catalog/fender-flares',
    urlsHint: 'Paste EGR Auto category URLs — one per line. Each category is fully expanded.',
  },
  {
    id: 'brown-davis',
    icon: ShoppingBag,
    label: 'Brown Davis',
    tagline: 'Walks the public vehicle selector, deduplicates tank part numbers, and exports Queensland RRP pricing.',
    storePlaceholder: BROWN_DAVIS_URL,
    urlsPlaceholder: BROWN_DAVIS_URL,
    hint: 'Restricted catalogue source. Prices always use the QLD RRP value.',
    storeHint: 'Scrapes every tank exposed by the Brown Davis public vehicle selector. Brown Davis notes that some products are not listed online.',
    urlsHint: 'Brown Davis is available as a whole-catalogue scrape only.',
  },
  {
    id: 'auto',
    icon: Wand2,
    label: 'Auto-detect',
    tagline: "Not sure which platform the store uses? Paste the URL and we'll figure it out before scraping.",
    storePlaceholder: 'https://mystore.com',
    urlsPlaceholder: 'https://mystore.com/products/my-product\nhttps://mystore.com/category/bikes',
    hint: "We'll check for BigCommerce, Shopify, Neto, Magento, or WordPress/WooCommerce signals on the storefront homepage.",
    storeHint: "Enter the store URL and we'll detect the platform automatically before starting.",
    urlsHint: "Paste product or category URLs — we'll detect the platform from the store homepage.",
  },
];

const MAX_PAGES_OPTIONS: { value: MaxPages; label: string }[] = [
  { value: 250,  label: '250 pages  (~5 min)' },
  { value: 500,  label: '500 pages  (~9 min)' },
  { value: 1000, label: '1,000 pages (~18 min)' },
  { value: 2000, label: '2,000 pages (~37 min)' },
  { value: 0,    label: 'All pages (2–4 hrs)' },
];

const MAX_PRODUCTS_OPTIONS: { value: MaxPages; label: string }[] = [
  { value: 250,  label: '250 products  (~5 min)' },
  { value: 500,  label: '500 products  (~10 min)' },
  { value: 1000, label: '1,000 products (~20 min)' },
  { value: 2000, label: '2,000 products (~40 min ⚠️)' },
  { value: 0,    label: 'No limit (may time out)' },
];

export function Scraper() {
  const { token } = useAuthStore();
  const authOpts = useAuthHeaders();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const { data: me } = useGetMe({
    request: authOpts,
    query: { enabled: !!token, retry: false },
  });
  const canUseBrownDavis = me?.email?.trim().toLowerCase() === BROWN_DAVIS_EMAIL;
  const visiblePlatforms = PLATFORMS.filter(p => p.id !== 'brown-davis' || canUseBrownDavis);

  const [platform, setPlatform] = useState<Platform>('bigcommerce');
  const [scrapeMode, setScrapeMode] = useState<ScrapeMode>('store');
  const [storeUrl, setStoreUrl] = useState('');
  const [urlsText, setUrlsText] = useState('');
  const [csvUploadText, setCsvUploadText] = useState('');
  const [csvFileName, setCsvFileName] = useState('');
  const [maxPages, setMaxPages] = useState<MaxPages>(500);
  const [maxProducts, setMaxProducts] = useState<MaxPages>(500);
  const [presetVendor, setPresetVendor] = useState('');
  const [presetProductType, setPresetProductType] = useState('');
  const [scraping, setScraping] = useState(false);
  const [phase, setPhase] = useState<'discovery' | 'scraping'>('discovery');
  const [discoveryCount, setDiscoveryCount] = useState(0);
  const [discoveryStep, setDiscoveryStep] = useState('');
  const [progress, setProgress] = useState<ProgressState | null>(null);
  const [csvText, setCsvText] = useState<string | null>(null);
  const [totalProducts, setTotalProducts] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [connectionLost, setConnectionLost] = useState(false);
  const [partialCsv, setPartialCsv] = useState<string | null>(null);
  const [partialCount, setPartialCount] = useState(0);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [repairStats, setRepairStats] = useState<{
    productsFixed: number;
    productsUnchanged: number;
    variantsBefore: number;
    variantsAfter: number;
  } | null>(null);
  const csvFileRef = useRef<HTMLInputElement>(null);
  const startTimeRef = useRef<number>(0);
  const receivedDoneRef = useRef(false);
  const abortRef = useRef<() => void>(() => {});

  const currentPlatform = PLATFORMS.find(p => p.id === platform)!;

  const parsedUrls = urlsText
    .split(/[\s\n\r]+/)
    .map(l => l.trim())
    .filter(l => l.startsWith('http'));

  const resetState = () => {
    setPhase('discovery');
    setDiscoveryCount(0);
    setDiscoveryStep('');
    setProgress(null);
    setCsvText(null);
    setError(null);
    setConnectionLost(false);
    setTotalProducts(0);
    setPartialCsv(null);
    setPartialCount(0);
    setSessionId(null);
    setWarnings([]);
    setRepairStats(null);
    receivedDoneRef.current = false;
  };

  const handlePlatformChange = (p: Platform) => {
    if (scraping) return;
    setPlatform(p);
    if (p === 'brown-davis') {
      setScrapeMode('store');
      setStoreUrl(BROWN_DAVIS_URL);
    } else {
      setStoreUrl('');
    }
    setUrlsText('');
    setCsvUploadText('');
    setCsvFileName('');
    setCsvText(null);
    setError(null);
    setConnectionLost(false);
  };

  const handleModeChange = (m: ScrapeMode) => {
    if (scraping) return;
    setScrapeMode(m);
    if (m === 'csv') setPlatform('shopify');
    setCsvText(null);
    setError(null);
    setConnectionLost(false);
    setRepairStats(null);
  };

  // ---------------------------------------------------------------------------
  // SSE stream reader — shared by both GET (store mode) and POST (URL list mode)
  // ---------------------------------------------------------------------------
  const readSseStream = async (res: Response, abortedRef: { current: boolean }) => {
    const reader = res.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done || abortedRef.current) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        try {
          const event = JSON.parse(line.slice(6));
          if (event.type === 'session') {
            setSessionId(event.sessionId);
          } else if (event.type === 'discover') {
            setDiscoveryCount(event.count ?? 0);
            setDiscoveryStep(event.step ?? '');
          } else if (event.type === 'status') {
            setDiscoveryStep(event.message ?? '');
          } else if (event.type === 'progress') {
            if (event.current === 0) continue;
            setPhase('scraping');
            setProgress({
              current: event.current,
              total: event.total,
              url: event.url ?? '',
              productsFound: event.productsFound ?? 0,
            });
          } else if (event.type === 'partial') {
            setPartialCsv(event.csv);
            setPartialCount(event.totalProducts ?? 0);
          } else if (event.type === 'done') {
            receivedDoneRef.current = true;
            setCsvText(event.csv);
            setTotalProducts(event.totalProducts);
            setPartialCsv(null);
            setProgress(null);
            if (typeof event.productsFixed === 'number') {
              setRepairStats({
                productsFixed: event.productsFixed,
                productsUnchanged: event.productsUnchanged ?? 0,
                variantsBefore: event.variantsBefore ?? 0,
                variantsAfter: event.variantsAfter ?? 0,
              });
            }
          } else if (event.type === 'warning') {
            if (event.message) setWarnings(prev => [...prev, event.message]);
          } else if (event.type === 'error') {
            setError(event.message);
            setProgress(null);
          }
        } catch { }
      }
    }
  };

  const startScrape = async () => {
    if (scrapeMode === 'store' && !storeUrl.trim()) {
      toast({ title: 'URL required', description: 'Please enter a store URL.', variant: 'destructive' });
      return;
    }
    if (scrapeMode === 'urls' && parsedUrls.length === 0) {
      toast({ title: 'URLs required', description: 'Paste at least one URL (starting with http).', variant: 'destructive' });
      return;
    }
    if (scrapeMode === 'urls' && parsedUrls.length > 500) {
      toast({ title: 'Too many URLs', description: 'Maximum 500 URLs per request.', variant: 'destructive' });
      return;
    }
    if (scrapeMode === 'csv' && !storeUrl.trim()) {
      toast({ title: 'Storefront URL required', description: 'Enter the Shopify storefront URL used by the CSV export.', variant: 'destructive' });
      return;
    }
    if (scrapeMode === 'csv' && !csvUploadText) {
      toast({ title: 'CSV required', description: 'Upload a Shopify collection CSV to repair.', variant: 'destructive' });
      return;
    }

    setScraping(true);
    resetState();
    startTimeRef.current = Date.now();

    let aborted = false;
    const abortedRef = { current: false };
    const controller = new AbortController();
    abortRef.current = () => {
      aborted = true;
      abortedRef.current = true;
      controller.abort();
    };

    try {
      if (scrapeMode === 'csv') {
        setDiscoveryStep(`Checking ${csvFileName || 'uploaded CSV'} against the source storefront…`);
        const repairRes = await fetch(`${BASE}/api/scraper/shopify-csv-fix`, {
          method: 'POST',
          headers: { ...authOpts.headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({ csvText: csvUploadText, storefrontUrl: storeUrl.trim() }),
          signal: controller.signal,
        });
        if (!repairRes.ok || !repairRes.body) {
          const repairData = await repairRes.json().catch(() => ({}));
          throw new Error(repairData.error ?? `CSV repair failed (${repairRes.status})`);
        }
        await readSseStream(repairRes, abortedRef);
        if (!aborted && !receivedDoneRef.current) setConnectionLost(true);
        return;
      }

      let res: Response;

      // ── Auto-detect: resolve platform before scraping ─────────────────────
      let resolvedPlatform = platform;
      let autoDetectedEffectiveOrigin: string | undefined;

      if (platform === 'auto') {
        setDiscoveryStep('Detecting platform…');
        const detectUrl = scrapeMode === 'store'
          ? storeUrl.trim()
          : new URL(parsedUrls[0]!).origin;
        const normalizedDetect = detectUrl.startsWith('http') ? detectUrl : 'https://' + detectUrl;
        const detectRes = await fetch(
          `${BASE}/api/scraper/detect?url=${encodeURIComponent(normalizedDetect)}`,
          { headers: authOpts.headers, signal: controller.signal },
        );
        if (!detectRes.ok) {
          const d = await detectRes.json().catch(() => ({ error: 'Detection failed' }));
          throw new Error(d.error ?? 'Could not detect platform');
        }
        const { platform: detected, effectiveOrigin: detectedEffectiveOrigin } =
          await detectRes.json() as { platform: Platform; effectiveOrigin?: string };
        resolvedPlatform = detected;
        autoDetectedEffectiveOrigin = detectedEffectiveOrigin;

        const platformLabel = PLATFORMS.find(p => p.id === detected)?.label ?? detected;
        if (detectedEffectiveOrigin) {
          const effectiveHost = new URL(detectedEffectiveOrigin).hostname;
          setDiscoveryStep(`Detected: ${platformLabel} (via ${effectiveHost}) — starting scrape…`);
        } else {
          setDiscoveryStep(`Detected: ${platformLabel} — starting scrape…`);
        }
        await new Promise(r => setTimeout(r, 800)); // brief pause so user sees the detected platform
      }

      if (scrapeMode === 'store') {
        // ── GET request (whole-store mode) ────────────────────────────────────
        let url = storeUrl.trim();
        if (!url.startsWith('http://') && !url.startsWith('https://')) url = 'https://' + url;

        const params = new URLSearchParams({ url });
        if (resolvedPlatform === 'neto') params.set('maxPages', String(maxPages));
        if (presetVendor.trim())      params.set('presetVendor',      presetVendor.trim());
        if (presetProductType.trim()) params.set('presetProductType', presetProductType.trim());
        // When auto-detected as Magento with a geo-redirect, pass the resolved origin
        // so the server can skip its own re-fetch and use GraphQL immediately.
        if (autoDetectedEffectiveOrigin && resolvedPlatform === 'magento') {
          params.set('effectiveOrigin', autoDetectedEffectiveOrigin);
        }

        const endpoint = resolvedPlatform === 'shopify' ? 'shopify'
          : resolvedPlatform === 'neto' ? 'neto'
          : resolvedPlatform === 'magento' ? 'magento'
          : resolvedPlatform === 'wordpress' ? 'wordpress'
          : resolvedPlatform === 'egr' ? 'egr'
          : resolvedPlatform === 'brown-davis' ? 'brown-davis'
          : 'bigcommerce';

        const apiUrl = `${BASE}/api/scraper/${endpoint}?${params}`;
        if (platform !== 'auto') {
          setDiscoveryStep(
            resolvedPlatform === 'shopify' ? 'Connecting to Shopify products API…'
            : resolvedPlatform === 'magento' ? 'Checking sitemap.xml…'
            : resolvedPlatform === 'wordpress' ? 'Checking WooCommerce sitemap…'
            : 'Checking robots.txt…'
          );
        }

        res = await fetch(apiUrl, { headers: authOpts.headers, signal: controller.signal });
      } else {
        // ── POST request (URL list mode) ──────────────────────────────────────
        setPhase('discovery');
        if (platform !== 'auto') setDiscoveryStep('Preparing URLs…');

        res = await fetch(`${BASE}/api/scraper/urls`, {
          method: 'POST',
          headers: { ...authOpts.headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            platform: resolvedPlatform,
            urls: parsedUrls,
            maxProducts: maxProducts || 0,
            presets: {
              vendor:      presetVendor.trim()      || undefined,
              productType: presetProductType.trim() || undefined,
            },
          }),
          signal: controller.signal,
        });
      }

      if (!res.ok || !res.body) {
        const data = await res.json().catch(() => ({ error: `Server error (${res.status})` }));
        throw new Error(data.error ?? 'Scrape failed');
      }

      await readSseStream(res, abortedRef);

      if (!aborted && !receivedDoneRef.current && !error) {
        setConnectionLost(true);
      }
    } catch (err: any) {
      if (!aborted) {
        const isNetworkDrop = err?.name === 'AbortError'
          ? false
          : (err?.message?.includes('network') || err?.message?.includes('fetch'));
        if (isNetworkDrop && !receivedDoneRef.current) {
          setConnectionLost(true);
        } else if (err?.name !== 'AbortError') {
          setError(err.message ?? 'Unknown error');
        }
      }
    } finally {
      setScraping(false);
    }
  };

  const resumeScrape = async (resumeSessionId: string) => {
    setScraping(true);
    setConnectionLost(false);
    setError(null);
    setProgress(null);
    receivedDoneRef.current = false;
    startTimeRef.current = Date.now();

    const abortedRef = { current: false };
    const controller = new AbortController();
    abortRef.current = () => { abortedRef.current = true; controller.abort(); };

    try {
      const res = await fetch(`${BASE}/api/scraper/resume/${resumeSessionId}`, {
        headers: authOpts.headers,
        signal: controller.signal,
      });

      if (!res.ok || !res.body) {
        const data = await res.json().catch(() => ({ error: `Server error (${res.status})` }));
        throw new Error(data.error ?? 'Resume failed');
      }

      await readSseStream(res, abortedRef);

      if (!abortedRef.current && !receivedDoneRef.current) {
        setConnectionLost(true);
      }
    } catch (err: any) {
      if (err?.name !== 'AbortError') {
        setError(err.message ?? 'Unknown error');
      }
    } finally {
      setScraping(false);
    }
  };

  const downloadCsv = () => {
    if (!csvText || isDownloading) return;
    setIsDownloading(true);
    // Yield to the browser so the button repaints before the Blob
    // allocation blocks the main thread on large files.
    setTimeout(() => {
      try {
        const blob = new Blob([csvText], { type: 'text/csv' });
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = `${scrapeMode === 'csv' ? 'shopify-options-fixed' : `${platform}-products`}-${Date.now()}.csv`;
        a.click();
        URL.revokeObjectURL(objectUrl);
      } finally {
        setIsDownloading(false);
      }
    }, 0);
  };

  const importIntoShopify = () => {
    if (!csvText) return;
    const filename = `${scrapeMode === 'csv' ? 'shopify-options-fixed' : `${platform}-products`}-${Date.now()}.csv`;
    sessionStorage.setItem('soraflo-scraped-csv', csvText);
    sessionStorage.setItem('soraflo-scraped-filename', filename);
    navigate('/import');
  };

  const pct = progress && progress.total > 0
    ? Math.round((progress.current / progress.total) * 100)
    : 0;

  const eta = (() => {
    if (!progress || progress.current === 0) return null;
    const elapsed = (Date.now() - startTimeRef.current) / 1000;
    const rate = progress.current / elapsed;
    const remaining = (progress.total - progress.current) / rate;
    return remaining;
  })();

  const displayUrl = (u: string) => {
    try {
      const parsed = new URL(u);
      const path = parsed.pathname;
      return path.length > 50 ? '…' + path.slice(-48) : path;
    } catch {
      return u.length > 55 ? u.slice(0, 52) + '…' : u;
    }
  };

  const sourceLabel = scrapeMode === 'urls'
    ? `${parsedUrls.length} URL${parsedUrls.length !== 1 ? 's' : ''}`
    : scrapeMode === 'csv'
      ? csvFileName || 'Uploaded Shopify CSV'
      : storeUrl.replace(/^https?:\/\//, '');

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-display font-bold text-gradient">Storefront Scraper</h1>
          <p className="text-muted-foreground mt-1">
            Select a platform, enter a store URL or paste specific product URLs, and export as a Shopify-ready CSV.
          </p>
        </div>

        {/* Platform selector */}
        <div className="glass-panel rounded-2xl p-6 space-y-4">
          <h2 className="font-semibold text-foreground text-sm">Platform</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {visiblePlatforms.map(p => {
              const Icon = p.icon;
              return (
                <button
                  key={p.id}
                  onClick={() => handlePlatformChange(p.id)}
                  disabled={scraping}
                  className={cn(
                    "relative rounded-xl border p-4 text-left transition-all duration-200",
                    "focus:outline-none focus:ring-2 focus:ring-primary/50",
                    platform === p.id
                      ? "border-primary/60 bg-primary/10 shadow-[0_0_0_1px_rgba(var(--primary-rgb),0.3)]"
                      : p.id === 'auto'
                        ? "border-primary/20 bg-primary/5 hover:bg-primary/10 hover:border-primary/30"
                        : "border-border bg-muted/50 hover:bg-muted/50 hover:border-border",
                    scraping && "opacity-50 cursor-not-allowed",
                  )}
                >
                  {platform === p.id && (
                    <span className="absolute top-3 right-3">
                      <CheckCircle className="w-4 h-4 text-primary" />
                    </span>
                  )}
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className={cn("w-4 h-4", platform === p.id ? "text-primary" : p.id === 'auto' ? "text-primary/60" : "text-muted-foreground")} />
                    <span className={cn("font-semibold text-sm", platform === p.id ? "text-foreground" : p.id === 'auto' ? "text-primary/80" : "text-foreground")}>
                      {p.label}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{p.tagline}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Scrape mode + input */}
        <div className="glass-panel rounded-2xl p-6 space-y-5">

          {/* Mode toggle */}
          {platform !== 'brown-davis' && (
          <div className="flex items-center gap-1 p-1 rounded-xl bg-muted/50 border border-border w-fit">
            <button
              onClick={() => handleModeChange('store')}
              disabled={scraping}
              className={cn(
                "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200",
                scrapeMode === 'store'
                  ? "bg-primary text-foreground shadow"
                  : "text-muted-foreground hover:text-foreground",
                scraping && "opacity-50 cursor-not-allowed",
              )}
            >
              <Globe className="w-3.5 h-3.5" />
              Whole Store
            </button>
            <button
              onClick={() => handleModeChange('urls')}
              disabled={scraping}
              className={cn(
                "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200",
                scrapeMode === 'urls'
                  ? "bg-primary text-foreground shadow"
                  : "text-muted-foreground hover:text-foreground",
                scraping && "opacity-50 cursor-not-allowed",
              )}
            >
              <List className="w-3.5 h-3.5" />
              Specific URLs
            </button>
            <button
              onClick={() => handleModeChange('csv')}
              disabled={scraping}
              className={cn(
                "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200",
                scrapeMode === 'csv'
                  ? "bg-primary text-foreground shadow"
                  : "text-muted-foreground hover:text-foreground",
                scraping && "opacity-50 cursor-not-allowed",
              )}
            >
              <FileText className="w-3.5 h-3.5" />
              Fix Shopify CSV
            </button>
          </div>
          )}

          {scrapeMode === 'csv' ? (
            <>
              <div>
                <h2 className="font-semibold text-foreground">Source Shopify storefront</h2>
                <p className="text-xs text-muted-foreground mt-1">
                  The export has product handles but not the custom selectors. Each matching public product page will be checked, while the rest of your CSV is retained.
                </p>
              </div>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <input
                  type="url"
                  value={storeUrl}
                  onChange={e => setStoreUrl(e.target.value)}
                  placeholder="https://haymanreese.com.au"
                  disabled={scraping}
                  className="w-full h-11 rounded-xl border border-border bg-muted/50 pl-10 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary transition-all disabled:opacity-50"
                />
              </div>
              <div
                role="button"
                tabIndex={0}
                onClick={() => !scraping && csvFileRef.current?.click()}
                onKeyDown={e => {
                  if ((e.key === 'Enter' || e.key === ' ') && !scraping) csvFileRef.current?.click();
                }}
                className={cn(
                  "rounded-xl border border-dashed p-6 text-center transition-colors cursor-pointer",
                  csvUploadText ? "border-primary/40 bg-primary/5" : "border-border hover:border-primary/40 hover:bg-muted/30",
                  scraping && "opacity-50 cursor-not-allowed",
                )}
              >
                <input
                  ref={csvFileRef}
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  disabled={scraping}
                  onChange={async e => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (!file.name.toLowerCase().endsWith('.csv') && file.type !== 'text/csv') {
                      toast({ title: 'Invalid file', description: 'Please upload a CSV file.', variant: 'destructive' });
                      return;
                    }
                    try {
                      const text = await file.text();
                      if (!text.trim()) throw new Error('The CSV file is empty.');
                      setCsvFileName(file.name);
                      setCsvUploadText(text);
                      setCsvText(null);
                      setError(null);
                      setRepairStats(null);
                    } catch (err: any) {
                      toast({ title: 'Could not read CSV', description: err.message, variant: 'destructive' });
                    }
                  }}
                />
                {csvUploadText ? (
                  <>
                    <CheckCircle className="w-7 h-7 text-primary mx-auto mb-2" />
                    <p className="font-medium text-foreground">{csvFileName}</p>
                    <p className="text-xs text-muted-foreground mt-1">CSV loaded — click to replace it</p>
                  </>
                ) : (
                  <>
                    <Upload className="w-7 h-7 text-muted-foreground mx-auto mb-2" />
                    <p className="font-medium text-foreground">Upload Shopify collection CSV</p>
                    <p className="text-xs text-muted-foreground mt-1">Click to browse for a .csv file</p>
                  </>
                )}
              </div>
              <div className="flex items-center gap-3">
                <Button
                  onClick={scraping ? () => abortRef.current() : startScrape}
                  variant={scraping ? 'outline' : 'primary'}
                  disabled={!scraping && (!csvUploadText || !storeUrl.trim())}
                >
                  {scraping ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Cancel</>
                  ) : (
                    <><Wand2 className="w-4 h-4 mr-2" />Fix Variant Options</>
                  )}
                </Button>
                <p className="text-xs text-muted-foreground">
                  Custom selectors are taken from source product forms; product fields are not rebuilt.
                </p>
              </div>
            </>
          ) : scrapeMode === 'store' ? (
            <>
              <h2 className="font-semibold text-foreground">Store URL</h2>
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                  <input
                    type="url"
                    value={storeUrl}
                    onChange={e => setStoreUrl(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !scraping) startScrape(); }}
                    placeholder={currentPlatform.storePlaceholder}
                    disabled={scraping || platform === 'brown-davis'}
                    className="w-full h-11 rounded-xl border border-border bg-muted/50 pl-10 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary transition-all disabled:opacity-50"
                  />
                </div>
                <Button
                  onClick={scraping ? () => abortRef.current() : startScrape}
                  variant={scraping ? 'outline' : 'primary'}
                  disabled={!token}
                  className="flex-shrink-0"
                >
                  {scraping ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Cancel</>
                  ) : (
                    <><Package className="w-4 h-4 mr-2" />Fetch Products</>
                  )}
                </Button>
              </div>

              {/* Neto page limit */}
              {platform === 'neto' && (
                <div className="flex items-center gap-3">
                  <label className="text-xs text-muted-foreground whitespace-nowrap">Page limit:</label>
                  <CompactSelect
                    value={maxPages}
                    onChange={e => setMaxPages(Number(e.target.value) as MaxPages)}
                    disabled={scraping}
                    className="h-8 text-xs"
                  >
                    {MAX_PAGES_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </CompactSelect>
                  <span className="text-xs text-muted-foreground">
                    Neto sitemaps mix products and categories — the scraper samples pages evenly and detects products automatically.
                  </span>
                </div>
              )}

              <p className="text-xs text-muted-foreground">{currentPlatform.storeHint}</p>
              {platform === 'brown-davis' && (
                <div className="rounded-xl border border-amber-500/20 bg-amber-500/10 px-4 py-3 text-xs text-amber-200/90">
                  QLD RRP is always used. Records without a readable QLD price or valid part number are skipped with a warning.
                  Brown Davis advises that some products are not listed in its public selector.
                </div>
              )}
            </>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <h2 className="font-semibold text-foreground">Product / Collection URLs</h2>
                {parsedUrls.length > 0 && (
                  <span className="text-xs text-primary font-medium">
                    {parsedUrls.length} URL{parsedUrls.length !== 1 ? 's' : ''} detected
                  </span>
                )}
              </div>

              <textarea
                value={urlsText}
                onChange={e => setUrlsText(e.target.value)}
                placeholder={currentPlatform.urlsPlaceholder}
                disabled={scraping}
                rows={6}
                className="w-full rounded-xl border border-border bg-muted/50 p-4 text-sm text-foreground placeholder:text-muted-foreground font-mono focus:outline-none focus:ring-1 focus:ring-primary transition-all disabled:opacity-50 resize-y"
              />

              <div className="flex items-center gap-3">
                <Button
                  onClick={scraping ? () => abortRef.current() : startScrape}
                  variant={scraping ? 'outline' : 'primary'}
                  disabled={!token}
                >
                  {scraping ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Cancel</>
                  ) : (
                    <><Package className="w-4 h-4 mr-2" />Scrape URLs</>
                  )}
                </Button>
                <p className="text-xs text-muted-foreground">{currentPlatform.urlsHint}</p>
              </div>

              {/* Product limit for Neto / BC / Magento — category expansion can yield thousands of URLs */}
              {(platform === 'neto' || platform === 'bigcommerce' || platform === 'magento') && (
                <div className="flex items-center gap-3">
                  <label className="text-xs text-muted-foreground whitespace-nowrap">Product limit:</label>
                  <CompactSelect
                    value={maxProducts}
                    onChange={e => setMaxProducts(Number(e.target.value) as MaxPages)}
                    disabled={scraping}
                    className="h-8 text-xs"
                  >
                    {MAX_PRODUCTS_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </CompactSelect>
                  <span className="text-xs text-muted-foreground">
                    Category URLs can expand to thousands of products — limit prevents proxy timeouts.
                  </span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Preset Fields */}
        <div className="glass-panel rounded-2xl p-6 space-y-4">
          <div>
            <h2 className="font-semibold text-foreground text-sm">Preset Fields <span className="font-normal text-muted-foreground">(optional)</span></h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Values entered here will override whatever is scraped from the store.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-foreground">Vendor</label>
              <input
                type="text"
                value={presetVendor}
                onChange={e => setPresetVendor(e.target.value)}
                disabled={scraping}
                placeholder="e.g. Offroad Animal"
                className="w-full h-9 rounded-xl border border-border bg-muted/50 px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary transition-all disabled:opacity-50"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-foreground">Product Type</label>
              <input
                type="text"
                value={presetProductType}
                onChange={e => setPresetProductType(e.target.value)}
                disabled={scraping}
                placeholder="e.g. Bull Bars"
                className="w-full h-9 rounded-xl border border-border bg-muted/50 px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary transition-all disabled:opacity-50"
              />
            </div>
          </div>
        </div>

        {/* Live progress panel */}
        {scraping && (
          <div className="glass-panel rounded-2xl p-6 space-y-5 border border-primary/20">

            {phase === 'discovery' && (
              <div className="space-y-5">
                <div className="flex items-center gap-3">
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-primary" />
                  </span>
                  <span className="text-sm font-medium text-foreground">
                    {scrapeMode === 'urls' && platform === 'shopify'
                      ? 'Expanding collections…'
                      : 'Discovering product pages…'}
                  </span>
                </div>

                <div className="flex items-end gap-3">
                  <span className="text-5xl font-bold font-mono tabular-nums text-foreground leading-none">
                    {discoveryCount.toLocaleString()}
                  </span>
                  <span className="text-lg text-muted-foreground mb-1">
                    {platform === 'shopify' ? 'products found' : platform === 'neto' ? 'candidate URLs found' : 'product URLs found'}
                  </span>
                </div>

                <p className="text-sm text-muted-foreground">
                  {discoveryStep || (platform === 'shopify' ? 'Fetching products from API…' : 'Reading sitemap to find all product pages…')}
                </p>

                <div className="w-full h-2 rounded-full bg-muted overflow-hidden">
                  <div className="h-full w-full rounded-full bg-primary/60 animate-pulse" />
                </div>

                {platform === 'neto' && scrapeMode === 'store' && (
                  <p className="text-xs text-muted-foreground">
                    Neto stores use a gzipped sitemap — decompressing and filtering candidate product pages now.
                  </p>
                )}
                {platform === 'shopify' && scrapeMode === 'store' && (
                  <p className="text-xs text-muted-foreground">
                    Using the Shopify products.json API — much faster than HTML scraping, no page-by-page crawling needed.
                  </p>
                )}
              </div>
            )}

            {phase === 'scraping' && progress && (
              <div className="space-y-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="relative flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-400" />
                    </span>
                    <span className="text-sm font-medium text-foreground">
                      {scrapeMode === 'csv' ? 'Repairing variant options' : 'Scraping in progress'}
                    </span>
                  </div>
                  <span className="text-sm font-mono text-primary font-semibold">{pct}%</span>
                </div>

                <div className="w-full h-3 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-primary to-emerald-400 transition-all duration-500"
                    style={{ width: `${pct}%` }}
                  />
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-xl bg-muted/50 border border-border p-3 text-center">
                    <p className="text-xs text-muted-foreground mb-1">
                      {scrapeMode === 'csv' ? 'Products checked' : 'Pages visited'}
                    </p>
                    <p className="text-lg font-bold font-mono text-foreground tabular-nums">
                      {progress.current.toLocaleString()}
                      <span className="text-muted-foreground font-normal text-sm"> / {progress.total.toLocaleString()}</span>
                    </p>
                  </div>
                  <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/20 p-3 text-center">
                    <p className="text-xs text-muted-foreground mb-1">
                      {scrapeMode === 'csv' ? 'Custom forms found' : 'Products found'}
                    </p>
                    <p className="text-lg font-bold font-mono text-emerald-400 tabular-nums">
                      {progress.productsFound.toLocaleString()}
                    </p>
                  </div>
                  <div className="rounded-xl bg-muted/50 border border-border p-3 text-center">
                    <p className="text-xs text-muted-foreground mb-1">Time remaining</p>
                    <p className="text-lg font-bold font-mono text-foreground tabular-nums">
                      {eta !== null ? fmtSecs(eta) : '–'}
                    </p>
                  </div>
                </div>

                {progress.url && (
                  <div className="flex items-center gap-2 rounded-xl bg-muted/50 border border-border px-3 py-2.5">
                    <Link className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                    <span className="text-xs text-muted-foreground font-mono truncate" title={progress.url}>
                      {displayUrl(progress.url)}
                    </span>
                  </div>
                )}

                <p className="text-xs text-muted-foreground">
                  {scrapeMode === 'csv'
                    ? 'Up to five public product forms are checked at once; image-only CSV rows are retained.'
                    : 'Pages are fetched one at a time with a short delay to respect the store’s server.'}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Connection lost warning */}
        {connectionLost && !scraping && (
          <div className="flex items-start gap-3 rounded-2xl border border-amber-500/20 bg-amber-500/10 p-5">
            <WifiOff className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1 space-y-3">
              <div>
                <p className="text-sm font-medium text-foreground">Connection dropped mid-scrape</p>
                <p className="text-sm text-amber-200/80 mt-1">
                  The scrape was interrupted.
                  {sessionId
                    ? ' Your progress has been saved — click "Resume scrape" to continue from where it left off.'
                    : partialCsv
                      ? ` ${partialCount.toLocaleString()} products were recovered before the drop.`
                      : ' Try again with fewer URLs or use the "Specific URLs" mode to scrape in smaller batches.'}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {sessionId && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => resumeScrape(sessionId)}
                  >
                    <RotateCw className="w-3.5 h-3.5 mr-1.5" />
                    Resume scrape
                  </Button>
                )}
                {partialCsv && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-amber-500/40 text-amber-200 hover:bg-amber-500/20"
                      onClick={() => {
                        const blob = new Blob([partialCsv], { type: 'text/csv' });
                        const objectUrl = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = objectUrl;
                        a.download = `${platform}-products-partial-${Date.now()}.csv`;
                        a.click();
                        URL.revokeObjectURL(objectUrl);
                      }}
                    >
                      <Download className="w-3.5 h-3.5 mr-1.5" />
                      Download {partialCount.toLocaleString()} recovered products
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-amber-200/70 hover:text-amber-200 hover:bg-amber-500/10"
                      onClick={() => {
                        sessionStorage.setItem('soraflo-scraped-csv', partialCsv);
                        sessionStorage.setItem('soraflo-scraped-filename', `${platform}-products-partial-${Date.now()}.csv`);
                        navigate('/import');
                      }}
                    >
                      Import {partialCount.toLocaleString()} products →
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Per-URL warnings (e.g. geo-restricted URLs, 404s) */}
        {warnings.length > 0 && (
          <div className="flex items-start gap-3 rounded-2xl border border-amber-500/20 bg-amber-500/10 p-5">
            <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1 space-y-1">
              <p className="text-sm font-medium text-foreground">
                {warnings.length === 1 ? 'One URL could not be fetched' : `${warnings.length} URLs could not be fetched`}
              </p>
              <ul className="space-y-1 mt-1">
                {warnings.map((w, i) => (
                  <li key={i} className="text-xs text-amber-200/80">{w}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="flex items-start gap-3 rounded-2xl border border-red-500/20 bg-red-500/10 p-5">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground">Scrape failed</p>
              <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* Results */}
        {csvText && (
          <div className="glass-panel rounded-2xl p-6 space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="font-semibold text-foreground">
                  {scrapeMode === 'csv' ? 'CSV repair complete' : 'Scrape complete'}
                </p>
                <p className="text-sm text-muted-foreground">
                  {scrapeMode === 'csv'
                    ? `${repairStats?.productsFixed ?? 0} product${repairStats?.productsFixed === 1 ? '' : 's'} had variant options repaired`
                    : `${totalProducts.toLocaleString()} product${totalProducts !== 1 ? 's' : ''} extracted and formatted as a Shopify CSV`}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              {[
                {
                  label: scrapeMode === 'csv' ? 'Products checked' : 'Products',
                  value: totalProducts.toLocaleString(),
                  icon: BarChart3,
                },
                { label: 'Source', value: sourceLabel, icon: Globe },
                {
                  label: scrapeMode === 'csv' ? 'Variant rows' : 'Format',
                  value: scrapeMode === 'csv'
                    ? `${repairStats?.variantsBefore ?? 0} → ${repairStats?.variantsAfter ?? 0}`
                    : 'Shopify CSV',
                  icon: CheckCircle,
                },
              ].map(({ label, value, icon: Icon }) => (
                <div key={label} className="rounded-xl bg-muted/50 border border-border p-4">
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <p className="text-sm font-medium text-foreground mt-1 truncate" title={value}>{value}</p>
                </div>
              ))}
            </div>

            <div className="flex gap-3 flex-wrap">
              <Button variant="outline" onClick={downloadCsv} disabled={isDownloading} className="flex-1 sm:flex-none">
                {isDownloading
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Preparing…</>
                  : <><Download className="w-4 h-4 mr-2" />Download CSV</>
                }
              </Button>
              <Button onClick={importIntoShopify} className="flex-1 sm:flex-none">
                Import into Shopify
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>

            <p className="text-xs text-muted-foreground">
              "Import into Shopify" opens the product importer with this CSV pre-loaded — select your destination store, review the column mapping, and run the import.
            </p>
          </div>
        )}

        {/* How it works */}
        {!scraping && !csvText && !error && !connectionLost && (
          <div className="glass-panel rounded-2xl p-6 space-y-4 border border-border/50">
            <h3 className="font-semibold text-foreground text-sm">How it works</h3>
            <ol className="space-y-3 text-sm text-muted-foreground list-none">
              {(scrapeMode === 'csv'
                ? [
                  { n: '1', text: 'Upload the original Shopify collection CSV and enter the public source storefront. Handles are used to find the matching product pages.' },
                  { n: '2', text: 'The repair checks product forms for custom selectors such as ECU and Tail. Products without custom selectors are left unchanged.' },
                  { n: '3', text: 'Only Option1, Option2, and Option3 cells are replaced. Existing titles, descriptions, SKUs, prices, images, and all other CSV fields stay in place.' },
                ]
                : platform === 'bigcommerce'
                ? scrapeMode === 'store'
                  ? [
                    { n: '1', text: "The scraper reads the store's public XML sitemap to discover every product page URL — no login or API credentials required." },
                    { n: '2', text: 'Each product page is fetched and parsed. Data is extracted from JSON-LD structured data, BCData inline JavaScript, and Open Graph meta tags.' },
                    { n: '3', text: 'All extracted products are assembled into a Shopify-compatible CSV. You can download it or send it straight to the product importer.' },
                  ]
                  : [
                    { n: '1', text: 'Paste one or more BigCommerce product URLs — one per line. Category URLs will be scanned for product links.' },
                    { n: '2', text: 'Each URL is fetched and scraped individually using JSON-LD, BCData, and Open Graph extraction.' },
                    { n: '3', text: 'All found products are assembled into a Shopify-compatible CSV ready to download or import.' },
                  ]
                : platform === 'neto'
                  ? scrapeMode === 'store'
                    ? [
                      { n: '1', text: "Neto stores publish a gzipped sitemap with all page types mixed together. The scraper decompresses it and pre-filters obvious non-product URLs." },
                      { n: '2', text: "The selected number of candidate pages are visited (sampled evenly across the store). Product pages are detected automatically via GA4 data attributes — category pages are skipped." },
                      { n: '3', text: 'Product title, SKU, price, vendor, category, and images are extracted and assembled into a Shopify-compatible CSV.' },
                    ]
                    : [
                      { n: '1', text: 'Paste specific Neto product page URLs — one per line. These are visited directly without any sitemap discovery step.' },
                      { n: '2', text: 'Each page is scraped using GA4 data attributes, microdata, and Open Graph tags to extract product details.' },
                      { n: '3', text: 'Products are assembled into a Shopify-compatible CSV for download or direct import.' },
                    ]
                  : scrapeMode === 'store'
                    ? [
                      { n: '1', text: "The scraper calls the store's public /products.json API — no credentials needed. This is far faster than HTML scraping since the data is already structured." },
                      { n: '2', text: 'All pages of results are fetched automatically (250 products per page). Full variant details including SKUs, pricing, and inventory are included.' },
                      { n: '3', text: 'Products are assembled into a Shopify-compatible CSV complete with options, variants, images, and barcodes.' },
                    ]
                    : [
                      { n: '1', text: 'Paste specific Shopify product URLs or collection URLs — one per line. Collection URLs are automatically expanded to all their products.' },
                      { n: '2', text: 'Product URLs are fetched via the /products/handle.json API endpoint — no HTML parsing needed, all variant data included.' },
                      { n: '3', text: 'Products are assembled into a Shopify-compatible CSV ready to import into any other Shopify store.' },
                    ]
              ).map(({ n, text }) => (
                <li key={n} className="flex items-start gap-3">
                  <span className={cn(
                    "flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold mt-0.5",
                    "bg-primary/20 text-primary",
                  )}>
                    {n}
                  </span>
                  <span>{text}</span>
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>
    </Layout>
  );
}
